import type { UserAddressInput } from '../../types/api';
import type { UserAddress } from '../../types';
import { MAX_USER_ADDRESSES, sortAddresses } from '../../utils/address';
import { tokenStorage } from '../../api/tokenStorage';
import { delay, mockStorageKeys, parseMockToken, readJson, writeJson } from '../mock/storage';

type StoredAddress = UserAddress & { userId: string };

function getMockSessionUserId(): string {
  const userId = parseMockToken(tokenStorage.get() ?? '');
  if (!userId) {
    throw new Error('Oturum gerekli.');
  }
  return userId;
}

function readAllAddresses(): StoredAddress[] {
  return readJson<StoredAddress[]>(mockStorageKeys.addresses, []);
}

function writeAllAddresses(addresses: StoredAddress[]) {
  writeJson(mockStorageKeys.addresses, addresses);
}

function normalizeDefaults(addresses: StoredAddress[], targetId?: string) {
  if (addresses.length === 0) return;

  const hasShippingDefault = addresses.some((address) => address.isDefaultShipping);
  const hasBillingDefault = addresses.some((address) => address.isDefaultBilling);

  if (!hasShippingDefault) {
    const first = targetId
      ? addresses.find((address) => address.id === targetId) ?? addresses[0]
      : addresses[0];
    first.isDefaultShipping = true;
  }

  if (!hasBillingDefault) {
    const first = targetId
      ? addresses.find((address) => address.id === targetId) ?? addresses[0]
      : addresses[0];
    first.isDefaultBilling = true;
  }
}

function toUserAddress(address: StoredAddress): UserAddress {
  const { userId: _userId, ...rest } = address;
  return rest;
}

export async function mockFetchAddresses(): Promise<UserAddress[]> {
  await delay(200);
  const userId = getMockSessionUserId();
  const addresses = readAllAddresses().filter((address) => address.userId === userId);
  return sortAddresses(addresses.map(toUserAddress));
}

export async function mockCreateAddress(input: UserAddressInput): Promise<UserAddress> {
  await delay(250);
  const userId = getMockSessionUserId();
  const all = readAllAddresses();
  const userAddresses = all.filter((address) => address.userId === userId);

  if (userAddresses.length >= MAX_USER_ADDRESSES) {
    throw new Error('En fazla 20 adres kaydedebilirsiniz.');
  }

  const now = new Date().toISOString();
  const isFirst = userAddresses.length === 0;
  const address: StoredAddress = {
    id: crypto.randomUUID(),
    userId,
    label: input.label.trim(),
    firstName: input.firstName.trim(),
    lastName: input.lastName.trim(),
    phone: input.phone.trim(),
    address: input.address.trim(),
    district: input.district.trim(),
    city: input.city.trim(),
    zip: input.zip.trim(),
    country: input.country.trim() || 'TR',
    isDefaultShipping: isFirst ? true : Boolean(input.isDefaultShipping),
    isDefaultBilling: isFirst ? true : Boolean(input.isDefaultBilling),
    createdAt: now,
    updatedAt: now,
  };

  if (address.isDefaultShipping) {
    for (const item of all) {
      if (item.userId === userId) item.isDefaultShipping = false;
    }
  }
  if (address.isDefaultBilling) {
    for (const item of all) {
      if (item.userId === userId) item.isDefaultBilling = false;
    }
  }

  all.push(address);
  normalizeDefaults(all.filter((item) => item.userId === userId), address.id);
  writeAllAddresses(all);
  return toUserAddress(address);
}

export async function mockUpdateAddress(id: string, input: UserAddressInput): Promise<UserAddress> {
  await delay(200);
  const userId = getMockSessionUserId();
  const all = readAllAddresses();
  const index = all.findIndex((address) => address.id === id && address.userId === userId);
  if (index === -1) {
    throw new Error('Adres bulunamadı.');
  }

  const updated: StoredAddress = {
    ...all[index],
    label: input.label.trim(),
    firstName: input.firstName.trim(),
    lastName: input.lastName.trim(),
    phone: input.phone.trim(),
    address: input.address.trim(),
    district: input.district.trim(),
    city: input.city.trim(),
    zip: input.zip.trim(),
    country: input.country.trim() || 'TR',
    isDefaultShipping: Boolean(input.isDefaultShipping),
    isDefaultBilling: Boolean(input.isDefaultBilling),
    updatedAt: new Date().toISOString(),
  };

  if (updated.isDefaultShipping) {
    for (const item of all) {
      if (item.userId === userId) item.isDefaultShipping = false;
    }
    updated.isDefaultShipping = true;
  }
  if (updated.isDefaultBilling) {
    for (const item of all) {
      if (item.userId === userId) item.isDefaultBilling = false;
    }
    updated.isDefaultBilling = true;
  }

  all[index] = updated;
  normalizeDefaults(all.filter((item) => item.userId === userId), updated.id);
  writeAllAddresses(all);
  return toUserAddress(updated);
}

export async function mockDeleteAddress(id: string): Promise<void> {
  await delay(200);
  const userId = getMockSessionUserId();
  const all = readAllAddresses();
  const index = all.findIndex((address) => address.id === id && address.userId === userId);
  if (index === -1) {
    throw new Error('Adres bulunamadı.');
  }

  all.splice(index, 1);
  normalizeDefaults(all.filter((item) => item.userId === userId));
  writeAllAddresses(all);
}

export async function mockSetDefaultShipping(id: string): Promise<UserAddress> {
  await delay(150);
  const userId = getMockSessionUserId();
  const all = readAllAddresses();
  const target = all.find((address) => address.id === id && address.userId === userId);
  if (!target) {
    throw new Error('Adres bulunamadı.');
  }

  for (const address of all) {
    if (address.userId === userId) {
      address.isDefaultShipping = address.id === id;
    }
  }

  target.updatedAt = new Date().toISOString();
  writeAllAddresses(all);
  return toUserAddress(target);
}

export async function mockSetDefaultBilling(id: string): Promise<UserAddress> {
  await delay(150);
  const userId = getMockSessionUserId();
  const all = readAllAddresses();
  const target = all.find((address) => address.id === id && address.userId === userId);
  if (!target) {
    throw new Error('Adres bulunamadı.');
  }

  for (const address of all) {
    if (address.userId === userId) {
      address.isDefaultBilling = address.id === id;
    }
  }

  target.updatedAt = new Date().toISOString();
  writeAllAddresses(all);
  return toUserAddress(target);
}
